import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Name", type: "text", required: true },
  { name: "company", label: "Company", type: "text", required: false },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone", type: "tel", required: false },
];

const empty = { name: "", company: "", email: "", phone: "", message: "", website: "" };

export const ContactForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — your message is on its way. Bruce will be in touch.");
      setForm(empty);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("Too many submissions. Please try again shortly, or give me a call.");
      } else {
        toast.error("Something went wrong. Try again, or give me a call.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-border bg-white px-4 py-3 text-base text-charcoal outline-none transition-colors duration-150 placeholder:text-muted-foreground focus:border-charcoal";

  return (
    <form data-testid="contact-form" onSubmit={submit} className="w-full">
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={form.website}
        onChange={update("website")}
        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {FIELDS.map((f) => (
          <div key={f.name} className={f.name === "message" ? "sm:col-span-2" : ""}>
            <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">
              {f.label}
              {f.required && <span className="text-charcoal"> *</span>}
            </label>
            <input
              data-testid={`contact-form-${f.name}`}
              type={f.type}
              value={form[f.name]}
              onChange={update(f.name)}
              required={f.required}
              className={inputCls}
            />
          </div>
        ))}
        <div className="sm:col-span-2">
          <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">
            Message<span className="text-charcoal"> *</span>
          </label>
          <textarea
            data-testid="contact-form-message"
            value={form.message}
            onChange={update("message")}
            required
            rows={5}
            className={`${inputCls} resize-none`}
          />
        </div>
      </div>

      <button
        data-testid="contact-submit"
        type="submit"
        disabled={loading}
        className="mt-6 inline-flex items-center justify-center gap-2 bg-charcoal px-8 py-4 text-sm uppercase tracking-[0.16em] text-white transition-colors duration-150 hover:bg-[#333] disabled:opacity-60"
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {loading ? "Sending" : "Send message"}
      </button>
    </form>
  );
};
